import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { useContext, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import Sidebar from "./Sidebar";

import { TenantAndCommerceContext } from "@main/contexts/TenantAndCommerceContext";
import { t } from "i18next";
import {
  Gear,
  ArrowSquareUp,
  ArrowSquareDown,
  Notepad,
  Browsers,
  Article,
  UserCircle,
  Truck,
  UserCirclePlus,
  Barcode,
  Tag,
  ShoppingCart,
  SealPercent,
  FilePdf,
  CirclesThreePlus,
  HandCoins,
} from "@phosphor-icons/react";

import { getApi } from "@beeldit/core/helpers/api";
import useGetConfigurationAction from "@main/configurations/actions/get/useGetConfigurationAction";
import { ConfigurationsContext } from "@main/contexts/ConfigurationsContext";
import { Users } from "@phosphor-icons/react/dist/ssr";
import useGetCommerceAction from "@main/commerces/actions/get-config/useGetCommerceAction";
import useGetTenantAction from "@main/tenants/actions/get/useGetTenantAction";
import useGetMe from "@beeldit/user-and-access/auth/services/useGetMe";
import useGetInPaymentConfirmationAction from "@beeldit/ecommerce/super-orders/actions/getInPaymentConfirmation/useGetInPaymentConfirmationAction";
import usePendingDealersAction from "@beeldit/user-and-access/users/actions/pending-dealers/usePendingDealersAction";

function Panel() {
  const { tokenLoaded, user } = useContext(AuthContext);

  const { getConfigurationFunction } = useGetConfigurationAction();
  const { setLangs, setSchemas } = useContext(ConfigurationsContext);

  const getMeFunction = useGetMe();

  /** TODO - Encapsular en una logica aparte que además compruebe que estos parametros son numericos */
  const { setTenantId } = useContext(TenantAndCommerceContext);
  const { setCommerceId } = useContext(TenantAndCommerceContext);
  const { getCommerceFunction } = useGetCommerceAction();
  const { getTenantFunction } = useGetTenantAction();

  let { tenantId, commerceId } = useParams();

  const {
    getFunction: getPaymentConfirmation,
    actionTemplate: getPaymentConfirmationActionTemplate,
  } = useGetInPaymentConfirmationAction();

  const {
    getFunction: getPendingDealers,
    actionTemplate: pendingDealersActionTemplate,
  } = usePendingDealersAction();

  useEffect(() => {
    if (!tenantId) {
      tenantId = "1";
    }
    setTenantId(parseInt(tenantId));
    getApi().defaults.headers.common["tenantId"] = tenantId;
    if (!commerceId) {
      commerceId = "1";
    }
    setCommerceId(parseInt(commerceId));
    getApi().defaults.headers.common["commerceId"] = commerceId;
  }, []);

  useEffect(() => {
    if (!tenantId || !commerceId || !tokenLoaded) return;
    getCommerceFunction(parseInt(commerceId));
    getTenantFunction(parseInt(tenantId));
  }, [commerceId, tokenLoaded]);

  useEffect(() => {
    let intervalId: any;

    if (tokenLoaded) {
      getConfigurationFunction("langs", setLangs);
      getConfigurationFunction("schemas", setSchemas);
      getMeFunction();
      getPaymentConfirmation(); // pedidos: llamada inmediata
      if (user && user.role === "admin") {
        getPendingDealers(); // dealers pendientes: llamada inmediata
      }

      intervalId = setInterval(() => {
        getPaymentConfirmation(); // pedidos: cada 3 minutos
        if (user && user.role === "admin") {
          getPendingDealers(); // dealers pendientes: cada 3 minutos
        }
      }, 1000 * 60 * 3); // 180000 ms = 3 minutos
    }

    // Limpiar el intervalo al desmontar o cambiar tokenLoaded
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [tokenLoaded]);

  if (!tokenLoaded || !user) {
    return <div>Loading...</div>;
  }

  const items: any[] = [
    {
      label: t("super_orders"),
      to: `/panel/${tenantId}/${commerceId}/admin/orders`,
      icon: <ShoppingCart size={24} />,
      roles: ["admin"],
      children_component: getPaymentConfirmationActionTemplate,
    },
    {
      label: t("orders"),
      to: `/panel/${tenantId}/${commerceId}/producer/orders`,
      icon: <ShoppingCart size={24} />,
      roles: ["producer"],
      children_component: getPaymentConfirmationActionTemplate,
    },
    {
      label: t("invoice_orders"),
      to: `/panel/${tenantId}/${commerceId}/producer/invoice-orders`,
      icon: <FilePdf size={24} />,
      roles: ["producer"],
    },
    {
      label: t("balances"),
      to: `/panel/${tenantId}/${commerceId}/producer/balances`,
      icon: <HandCoins size={24} />,
      roles: ["producer"],
    },
    {
      label: t("products"),
      to: `/panel/${tenantId}/${commerceId}/products`,
      icon: <Barcode size={24} />,
      roles: ["admin", "producer"],
    },
    {
      label: t("producers"),
      to: `/panel/${tenantId}/${commerceId}/producers`,
      icon: <UserCirclePlus size={24} />,
      roles: ["admin", "tenant_admin"],
    },
    {
      label: t("retails"),
      to: `/panel/${tenantId}/${commerceId}/retails`,
      icon: <UserCircle size={24} />,
      roles: ["admin", "tenant_admin"],
    },
    {
      label: t("dealers"),
      to: `/panel/${tenantId}/${commerceId}/dealers`,
      icon: <Truck size={24} />,
      roles: ["admin", "tenant_admin"],
      children_component: pendingDealersActionTemplate,
    },

    {
      label: t("coupons"),
      to: `/panel/${tenantId}/${commerceId}/coupons`,
      icon: <SealPercent size={24} />,
      roles: ["admin", "producer"],
    },
    {
      label: t("balances"),
      to: `/panel/${tenantId}/${commerceId}/balances`,
      icon: <HandCoins size={24} />,
      roles: ["admin", "tenant_admin"],
    },
    {
      label: t("pages"),
      to: `/panel/${tenantId}/${commerceId}/pages`,
      icon: <Browsers size={24} />,
      roles: ["admin", "tenant_admin"],
    },
    {
      label: t("tags"),
      to: `/panel/${tenantId}/${commerceId}/blog-categories`,
      icon: <Tag size={24} />,
      roles: ["admin"],
    },
    {
      label: t("blog"),
      to: `/panel/${tenantId}/${commerceId}/blog`,
      icon: <Article size={24} />,
      roles: ["admin"],
    },
    {
      label: (
        <>
          <Gear size={24} />
          {t("configurations")}
        </>
      ),
      roles: ["admin", "commerce_admin", "tenant_admin"],
      children: [
        {
          label: t("users"),
          to: `/panel/${tenantId}/${commerceId}/users`,
          icon: <Users size={24} />,
          roles: ["admin", "commerce_admin", "tenant_admin"],
        },
        {
          label: t("header"),
          to: `/panel/${tenantId}/${commerceId}/header`,
          icon: <ArrowSquareUp size={24} />,
          roles: ["admin", "tenant_admin"],
        },
        {
          label: t("footer"),
          to: `/panel/${tenantId}/${commerceId}/footer`,
          icon: <ArrowSquareDown size={24} />,
          roles: ["admin", "tenant_admin"],
        },
        {
          label: t("categories"),
          to: `/panel/${tenantId}/${commerceId}/categories`,
          icon: <Tag size={24} />,
          roles: ["admin", "tenant_admin"],
        },
        {
          label: t("options"),
          to: `/panel/${tenantId}/${commerceId}/options`,
          icon: <CirclesThreePlus size={24} />,
          roles: ["admin", "tenant_admin"],
        },
        {
          label: t("configurations"),
          to: `/panel/${tenantId}/${commerceId}/configurations`,
          icon: <Gear size={24} />,
          roles: ["admin"],
        },
      ],
    },
    {
      label: t("profile"),
      to: `/panel/${tenantId}/${commerceId}/producer/profile`,
      icon: <UserCircle size={24} />,
      roles: ["producer"],
    },
  ];

  return (
    <div>
      <div className="admin container-fluid">
        <div className="row">
          <Sidebar items={items} />
          <div className="page-content">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Panel;
